// @ts-ignore
import React, { useEffect, useMemo } from 'react';
import { UseSimpleStore, useSimpleStoreList, useTopicStoreReady } from '@om-fe/store';
import { MixedGroupedOddsOutrightsTabSelected } from '../../components/tabs/mixed-grouped-odds-outrights-tabs.types';
import { BemMethod, createBem, replaceTopicPlaceholders } from '@om-fe/utils';
import { MixedGroupedMatchList } from '../../components/match-list/mixed-grouped-match-list';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';
import { MixedGroupedOutrights } from '../../components/outrights/mixed-grouped-outrights';
import { MixedGroupedTournamentModel } from '../../mixed-grouped.types';
import { useMixedGroupedOddsStoreCreator } from '../../stores/mixed-grouped-odds.store';
import { useMixedGroupedLocationStoreCreator } from '../../stores/mixed-grouped-location.store';
import { useMixedGroupedTournamentsStoreCreator, UseMixedGroupedStore } from '../../mixed-grouped.store';
import { useSelectedTab } from '../mixed-grouped-container.hooks';
import { TournamentNav } from '../../../tournament-nav/tournament-nav';
import { useMixedGroupedCategoryStoreCreator } from '../../stores/mixed-grouped-category.store';
import { PlatformTypes } from '@om-fe/utils';
import { useMixedGroupedOutrightsStoreCreator } from '../../stores/mixed-grouped-outrights.store';
import { useParams } from 'react-router';
import RedirectTo from '@om-fe/core/components/redirect-to/redirect-to';
import { RouteNames, ParamModel } from '@om-fe/core/core/routing/utils';
import { TournamentParams } from '@om-fe/models';
import { ALL_TOURNAMENT_ID } from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav-constants';
import BannermatrixBanner from '@om-fe/core/components/external-widgets/bannermatrix-banner';
import getConfig, { BannerMatrixConfig } from '@om-fe/core/utils/config';
import { TOPIC_CATEGORY_TOURNAMENTS, TOPIC_LOCATION_TOURNAMENTS, TOPIC_LOCATIONS_TOURNAMENTS_CHILDREN } from '@om-fe/core/components/grouped-match-list/store/grouped-match-list.constants';

export interface MixedGroupedLocationPublicProps {
    readonly disciplineId: string;
    readonly locationId?: string;
    readonly eventCategoryId?: string;
    readonly tournamentId: string;
    readonly sportPageType: DisciplineRouteType;
    readonly activeTab?: string;
    readonly mainMarketsLimit?: number;
    readonly deviceType?: PlatformTypes;
    readonly handleTournamentParams?: (params: TournamentParams) => void;
    readonly tournamentParams: TournamentParams;
    readonly handleActiveTabOutrights?: () => void;
    readonly navLayout?: boolean;
}

interface RouteParams {
    readonly activeTab?: string;
    readonly disciplineId?: string;
    readonly disciplineName?: string;
    readonly locationId?: string;
    readonly locationName?: string;
    readonly eventCategoryId?: string;
    readonly eventCategoryName?: string;
    readonly tournamentId?: string;
    readonly tournamentName?: string;
}

const bem: BemMethod = createBem('MixedGroupedTournament');
export const MixedGroupedTournament = (
    {
        disciplineId,
        locationId = '',
        tournamentId,
        sportPageType,
        eventCategoryId = '',
        activeTab = '0',
        mainMarketsLimit,
        deviceType,
        handleTournamentParams,
        tournamentParams,
        handleActiveTabOutrights,
        navLayout = false,
    }: MixedGroupedLocationPublicProps) => {

        const mixedStoreTopic: string = useMemo(() => {
            if (tournamentId !== ALL_TOURNAMENT_ID) {
                return replaceTopicPlaceholders(TOPIC_LOCATIONS_TOURNAMENTS_CHILDREN, { tournamentId });
            }

            if (eventCategoryId) {
                return replaceTopicPlaceholders(TOPIC_CATEGORY_TOURNAMENTS, { eventCategoryId });
            }

            return replaceTopicPlaceholders(TOPIC_LOCATION_TOURNAMENTS, { disciplineId, locationId });
        }, [disciplineId, eventCategoryId, locationId, tournamentId]);

        const useMixedStore: UseMixedGroupedStore = useMixedGroupedTournamentsStoreCreator(mixedStoreTopic);
        const useOddsStore: UseSimpleStore<MixedGroupedTournamentModel> = useMixedGroupedOddsStoreCreator(useMixedStore);
        const useMixedStoreNavLocation: UseMixedGroupedStore = useMixedGroupedLocationStoreCreator(disciplineId, locationId);
        const useMixedStoreNavCategories: UseMixedGroupedStore = useMixedGroupedCategoryStoreCreator(eventCategoryId);
        const useTournamentStoreLocation: UseSimpleStore<MixedGroupedTournamentModel> = useMixedGroupedOutrightsStoreCreator(useMixedStoreNavLocation);
        const useTournamentStoreCategories: UseSimpleStore<MixedGroupedTournamentModel> = useMixedGroupedOutrightsStoreCreator(useMixedStoreNavCategories);
        const ready: boolean = useTopicStoreReady(useMixedStore);
        const oddsList: string[] = useSimpleStoreList(useOddsStore);
        const outrightsList: string[] = useSimpleStoreList(eventCategoryId === '' ? useTournamentStoreLocation : useTournamentStoreCategories);
        const useOddsStoreLocation: UseSimpleStore<MixedGroupedTournamentModel> = useMixedGroupedOddsStoreCreator(useMixedStoreNavLocation);
        const useOddsStoreCategories: UseSimpleStore<MixedGroupedTournamentModel> = useMixedGroupedOddsStoreCreator(useMixedStoreNavCategories);
        const bannerMatrixConfig: BannerMatrixConfig | undefined = getConfig().bannerMatrix;
        const routeParams = useParams<RouteParams>();

        const [selected] = useSelectedTab(
            oddsList,
            useMemo(() => ({
                tournamentId,
            }), [tournamentId]),
        );

        useEffect(() => {
            if (ready && activeTab === '0' && oddsList.length === 0 && outrightsList.length) {
                handleActiveTabOutrights?.();
            }
        }, [ready, activeTab, oddsList, outrightsList]);

        if (!activeTab) {
            const routeParamsRedirect: ParamModel = {
                ...routeParams,
            };

            return <RedirectTo routeName={eventCategoryId ? RouteNames.tournamentCategories : RouteNames.tournamentLocation} routeParams={routeParamsRedirect} />;
        }

        return (
            <div className={bem()}>
                <TournamentNav
                    useStore={eventCategoryId === '' ? useOddsStoreLocation : useOddsStoreCategories}
                    hasAllButton={true}
                    hasOutrightsButton={outrightsList.length > 0}
                    activeTab={activeTab === '0' ? selected : activeTab}
                    hasArrows={deviceType === 'desktop'}
                    handleTournamentParams={handleTournamentParams}
                    tournamentParams={tournamentParams}
                    navLayout={navLayout}
                />
                {!ready
                    ? (
                        <Spinner />
                    ) : (
                        <>
                            {bannerMatrixConfig && (
                                <>
                                    {bannerMatrixConfig.showOnTournament && tournamentId && tournamentId !== ALL_TOURNAMENT_ID && (
                                        <BannermatrixBanner
                                            className="BannerWidgetCenterTournament BannerWidgetColorPack"
                                            positionTag="center-tournament"
                                            pageTag="tournament-page"
                                            eventId="0"
                                            tournamentId={tournamentId}
                                        />
                                    )}
                                    {bannerMatrixConfig.showOnLocation && tournamentId && tournamentId === ALL_TOURNAMENT_ID && locationId && disciplineId && (
                                        <BannermatrixBanner
                                            className="BannerWidgetCenterLocation BannerWidgetColorPack"
                                            positionTag="center-location"
                                            pageTag="location-page"
                                            eventId="0"
                                            tournamentId="0"
                                            locationId={locationId}
                                            sportId={disciplineId}
                                        />
                                    )}
                                </>
                            )}
                            {activeTab === MixedGroupedOddsOutrightsTabSelected.Odds || activeTab === '0' && oddsList.length > 0
                                ? (
                                    <MixedGroupedMatchList useStore={useOddsStore} sportPageType={sportPageType} mainMarketsLimit={mainMarketsLimit} deviceType={deviceType} disciplineId={disciplineId} />
                                )
                                : (
                                    <MixedGroupedOutrights useStore={eventCategoryId === '' ? useTournamentStoreLocation : useTournamentStoreCategories} sportPageType={sportPageType} tournamentId={tournamentId} />
                                )
                            }
                        </>
                    )
                }
            </div>
        );
    };